import { Divider } from '@huggg/react-components';
import {
  PrimaryButton,
  SecondaryButton,
  Button as ButtonText,
  ButtonArrowState,
  Button as Body4,
} from '@huggg/react-components';
import classnames from 'classnames';
import { Pin, PhoneNumber } from 'components/forms';
import { PageTitle } from 'components/misc';
import {
  Countdown as UICountdown,
  Form as UIForm,
  InfoDialog as UIInfoDialog,
} from 'components/ui';
import config from 'config';
import dictionary from 'config/dictionary.json';
import { addSeconds } from 'date-fns';
import { withAuth, withRedirect } from 'hoc';
import { useAnalytics } from 'hooks';
import { Layout } from 'layouts';
import PropTypes from 'prop-types';
import { compose } from 'ramda';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import HistoryService from 'services/HistoryService';
import { interpolate } from 'utilities/interpolate';
import styles from './login.module.scss';

const history = new HistoryService();

const RETRY_TIMEOUT = 15;

export const ScreensLogin = (props) => {
  const {
    activationCode,
    beginSMSChallenge,
    challengeID,
    isLoading,
    resolveSMSChallenge,
    SMSStep,
    SMSTryCount,
  } = props;
  const [canRequestTimeout, setCanRequestTimeout] = useState(null);
  const [canRequest, setCanRequest] = useState(false);
  const [number, setNumber] = useState('');
  const [start, setStart] = useState(null);
  const [persistLogin, setPersistLogin] = useState(false);
  const pinDialogRef = useRef();
  const analytics = useAnalytics();

  const beginChallenge = (number, setFieldError) => {
    const initialCount = SMSTryCount + 1;
    const multiplier = initialCount < 3 ? initialCount : 3;

    setStart(new Date());
    setCanRequestTimeout(addSeconds(new Date(), RETRY_TIMEOUT * multiplier));
    beginSMSChallenge(number, setFieldError);
  };

  const onPhoneNumberSubmit = (values, { setFieldError, setSubmitting }) => {
    beginChallenge(values.number, setFieldError);
    setNumber(values.number);
    analytics.formSubmitSMSChallengePhoneNumber();

    setTimeout(() => setSubmitting(false), 500);
  };

  const onPinSubmit = (values, { setFieldError, setSubmitting }) => {
    resolveSMSChallenge(values.pin, challengeID, persistLogin, setFieldError);
    analytics.formSubmitSMSChallengePin();

    setTimeout(() => setSubmitting(false), 500);
  };

  const onRetryClick = () => {
    analytics.buttonClickSMSChallengeRetry();
    beginChallenge(number);
    setCanRequest(false);
  };

  const onWrongNumberClick = () => {
    analytics.buttonClickSMSChallengeWrongNumber();

    // Set timeout to give the analytics event some time to complete
    setTimeout(() => {
      window.location.reload();
    }, 15);
  };

  const onRememberMeChange = (event) => {
    const checked = event.target.checked;

    analytics.formEventLoginRememberMe(checked);
    setPersistLogin(checked);
  };

  const onAfterOpen = useCallback(() => {
    analytics.dialogViewedSMSChallengePin();
  }, [analytics]);

  useEffect(() => {
    pinDialogRef.current && activationCode && pinDialogRef.current.open();
  }, [activationCode]);

  useEffect(() => {
    analytics.screenViewedLogin();
  }, [analytics]);

  return (
    <>
      <Layout showBackButton={false} title={dictionary.loginPage.title}>
        <PageTitle title={dictionary.loginPage.pageTitle} />

        <div className={classnames('container', styles.containerRedesign)}>
          {SMSStep === 'begin' && (
            <div className={styles.page} data-test-id="login-page-phone">
              <h2 className={styles.title}>
                {dictionary.loginPage.phoneNumber.title}
              </h2>
              <div className={styles.description}>
                {dictionary.loginPage.phoneNumber.description}
              </div>

              <Divider />

              <PhoneNumber
                button={
                  <PrimaryButton
                    loading={isLoading}
                    label={dictionary.forms.phoneNumber.submitButton.toUpperCase()}
                    className={styles.phoneNumberButton}
                  />
                }
                onSubmit={onPhoneNumberSubmit}
              />
            </div>
          )}

          {SMSStep === 'respond' && (
            <div className={styles.page} data-test-id="login-page-pin">
              <h2 className={styles.title}>{dictionary.loginPage.pin.title}</h2>
              <div className={styles.description}>
                {interpolate(
                  dictionary.loginPage.pin.description,
                  '[phone_number]',
                  <Body4 style={{ display: 'inline' }}>{number}</Body4>,
                )}
              </div>

              <Pin
                button={
                  <PrimaryButton
                    label={dictionary.forms.pin.submitButton.toUpperCase()}
                    style={{ width: '280px' }}
                  />
                }
                onSubmit={onPinSubmit}
                focusOnMount={true}
                data-test-id="login-pin-form"
              />

              <SecondaryButton
                className={classnames(styles.actionButton, styles.retryButton)}
                disabled={!canRequest}
                onClick={onRetryClick}
                data-test-id="login-button-retry"
              >
                <ButtonText>
                  {dictionary.loginPage.pin.requestCodeButton}
                </ButtonText>
                {!canRequest && (
                  <UICountdown
                    className={styles.countdown}
                    end={canRequestTimeout}
                    start={start}
                    onExpiry={() => setCanRequest(true)}
                    showDays={false}
                    showHours={false}
                    showMinutes={false}
                    showSuffix={false}
                  />
                )}
              </SecondaryButton>

              <SecondaryButton
                className={classnames(
                  styles.actionButton,
                  styles.wrongNumberButton,
                )}
                onClick={onWrongNumberClick}
                label={dictionary.loginPage.pin.wrongNumberButton}
                arrow={ButtonArrowState.Backwards}
              />
            </div>
          )}

          <UIForm.Checkbox
            className={styles.rememberMe}
            name="remember-me"
            labelText="remember me on this device"
            onChange={onRememberMeChange}
            type="checkbox"
            data-test-id="login-remember-me"
          />
        </div>
      </Layout>

      {(config.env !== 'prod' || config.env !== 'production') && (
        <UIInfoDialog
          header="activation code"
          body={activationCode}
          onAfterOpen={onAfterOpen}
          ref={pinDialogRef}
        />
      )}
    </>
  );
};

ScreensLogin.propTypes = {
  beginSMSChallenge: PropTypes.func.isRequired,
  resolveSMSChallenge: PropTypes.func.isRequired,
  SMSStep: PropTypes.string.isRequired,
  activationCode: PropTypes.string,
  challengeID: PropTypes.string,
  isLoading: PropTypes.bool,
  SMSTryCount: PropTypes.number,
};

const redirectHandler = (props) => {
  const excludedPathnames = ['/login'];
  const previousPathname = history.getPreviousPathname();
  const route =
    previousPathname && !excludedPathnames.includes(previousPathname)
      ? previousPathname
      : '/';

  return [props.isAuthed, route];
};

export default compose(withAuth, withRedirect(redirectHandler))(ScreensLogin);
